/**
 * Ajoute un overlay transparent sur les iframes vidéo pour que Lenis
 * puisse intercepter le scroll. Au clic, l'overlay se retire, l'iframe
 * devient interactive et reçoit le clic. Quand la souris quitte le
 * conteneur, l'overlay revient.
 */
export function initVideoOverlay() {
  const containers = document.querySelectorAll(
    '.m-img-txt__video, .m-flex-video, .o-pole__video'
  );

  containers.forEach((container) => {
    const iframe = container.querySelector('iframe');
    if (!iframe) return;

    // Autoplay/pause au scroll pour les conteneurs marqués [data-autoplay]
    if (container.hasAttribute('data-autoplay')) {
      initAutoplay(container, iframe);
      return;
    }

    initOverlay(container, iframe);
  });
}

/**
 * Overlay classique pour Lenis (scroll passthrough)
 */
function initOverlay(container, iframe) {
  if (getComputedStyle(container).position === 'static') {
    container.style.position = 'relative';
  }

  const overlay = document.createElement('div');
  overlay.style.cssText =
    'position:absolute;inset:0;z-index:2;cursor:pointer;';

  container.appendChild(overlay);

  overlay.addEventListener('click', (e) => {
    overlay.style.display = 'none';

    iframe.contentWindow.postMessage(
      { event: 'command', func: 'playVideo' },
      '*'
    );

    const clickTarget = document.elementFromPoint(e.clientX, e.clientY);
    if (clickTarget && clickTarget !== overlay) {
      clickTarget.dispatchEvent(
        new MouseEvent('click', { bubbles: true, clientX: e.clientX, clientY: e.clientY })
      );
    }
  });

  container.addEventListener('mouseleave', () => {
    overlay.style.display = '';
  });
}

/**
 * Charge l'API YouTube IFrame Player et lance/pause la vidéo
 * automatiquement selon la visibilité du conteneur.
 */
function initAutoplay(container, iframe) {
  // Injecter enablejsapi + mute dans l'URL de l'iframe oEmbed
  const url = new URL(iframe.src);
  url.searchParams.set('enablejsapi', '1');
  url.searchParams.set('mute', '1');
  url.searchParams.set('origin', window.location.origin);
  iframe.src = url.toString();

  // Donner un id unique à l'iframe pour YT.Player
  if (!iframe.id) {
    iframe.id = 'yt-autoplay-' + Math.random().toString(36).slice(2, 8);
  }

  loadYouTubeAPI(() => {
    const player = new YT.Player(iframe.id, {
      events: {
        onReady: () => {
          player.mute();

          const observer = new IntersectionObserver(
            ([entry]) => {
              if (entry.isIntersecting) {
                player.playVideo();
              } else {
                player.pauseVideo();
              }
            },
            { threshold: 0.5 }
          );

          observer.observe(container);
        },
      },
    });
  });
}

/**
 * Charge le script YouTube IFrame API une seule fois,
 * puis exécute le callback.
 */
let ytApiCallbacks = [];
let ytApiLoading = false;

function loadYouTubeAPI(callback) {
  if (window.YT && window.YT.Player) {
    callback();
    return;
  }

  ytApiCallbacks.push(callback);

  if (ytApiLoading) return;
  ytApiLoading = true;

  const tag = document.createElement('script');
  tag.src = 'https://www.youtube.com/iframe_api';
  document.head.appendChild(tag);

  window.onYouTubeIframeAPIReady = () => {
    ytApiCallbacks.forEach((cb) => cb());
    ytApiCallbacks = [];
  };
}
